import { Injectable } from '@angular/core';
import { CharterColor } from '../models/charter-color.model';
import { CHARTER_COLOR_FALLBACK } from '../constants/charter-color.defaults';

/** Applique la charte couleur sur :root (équivalent ThemeData + widgets mobile). */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private current: CharterColor = CHARTER_COLOR_FALLBACK;

  apply(charter: CharterColor): void {
    this.current = charter;
    const root = document.documentElement;
    const isDark = charter.mode === 'dark';
    root.setAttribute('data-theme', isDark ? 'dark' : 'light');
    const set = (name: string, value: string) => root.style.setProperty(name, value);

    set('--charter-principal', charter.principal);
    set('--charter-secondary', charter.secondary);
    set('--charter-primary-bg', charter.primaryBg);
    set('--charter-secondary-bg', charter.secondaryBg);
    set('--charter-primary-text', charter.primaryText);
    set('--charter-secondary-text', charter.secondaryText);
    set('--charter-disable-text', charter.disableText);
    set('--charter-light-text', charter.lightText);
    set('--charter-success', charter.success);
    set('--charter-warning', charter.warning);
    set('--charter-error', charter.error);
    set('--charter-info', charter.info);
    set('--charter-exception', charter.exception);
    set('--charter-primary-card-bg', charter.primaryCardBg);
    set('--charter-secondary-card-bg', charter.secondaryCardBg);

    // Aliases sémantiques (composants web)
    set('--color-primary', charter.principal);
    set('--color-secondary', charter.secondary);
    set('--color-primary-dark', this.shade(charter.principal, -12));
    set('--color-bg-page', charter.primaryBg);
    set('--color-bg-surface', charter.primaryCardBg);
    set('--color-bg-muted', charter.secondaryBg);
    set('--color-bg-header', charter.primaryCardBg);
    set('--color-bg-input', charter.primaryCardBg);
    set('--color-bg-footer', charter.principal);
    set('--color-text', charter.primaryText);
    set('--color-text-muted', charter.disableText);
    set('--color-text-on-primary', charter.lightText);
    set('--color-text-footer', this.withAlpha(charter.lightText, 0.82));
    set('--color-text-footer-strong', charter.lightText);
    set('--color-accent', charter.secondary);
    set('--color-accent-text', charter.secondaryText);
    set('--color-success', charter.success);
    set('--color-warning', charter.warning);
    set('--color-error', charter.error);
    set('--color-info', charter.info);
    set('--color-border', this.withAlpha(charter.primaryText, 0.08));
    set('--color-border-subtle', this.withAlpha(charter.primaryText, 0.06));
    set('--color-border-strong', this.withAlpha(charter.primaryText, 0.12));
    set('--color-shadow', isDark ? 'rgba(0, 0, 0, 0.35)' : 'rgba(0, 0, 0, 0.08)');
    set('--color-overlay', isDark ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.28)');
    set('--color-hover-surface', this.withAlpha(charter.principal, 0.06));
    set('--color-placeholder', charter.disableText);
    set('--color-error-bg', this.withAlpha(charter.error, 0.08));
    set('--color-error-text', charter.error);
    set('--color-success-text', charter.success);
    set('--color-gradient-hero', `linear-gradient(135deg, ${this.shade(charter.principal, -25)} 0%, ${charter.principal} 55%, ${charter.secondary} 100%)`);
    set('--color-gradient-page', `linear-gradient(180deg, ${charter.primaryBg} 0%, ${charter.secondaryBg} 38%, ${charter.primaryCardBg} 100%)`);
    set('--color-focus-ring', this.withAlpha(charter.principal, 0.12));
    set('--color-primary-faint', this.withAlpha(charter.principal, 0.06));
    set('--color-primary-soft', this.withAlpha(charter.principal, 0.12));
    set('--color-primary-muted', this.withAlpha(charter.principal, 0.45));
    set('--color-primary-shadow', this.withAlpha(charter.principal, 0.28));
    set('--color-primary-shadow-strong', this.withAlpha(charter.principal, 0.32));
    set('--color-secondary-faint', this.withAlpha(charter.secondary, 0.1));
    set('--color-secondary-soft', this.withAlpha(charter.secondary, 0.12));
    set('--color-secondary-muted', this.withAlpha(charter.secondary, 0.45));
    set('--color-secondary-shadow', this.withAlpha(charter.secondary, 0.15));

    // intl-tel-input
    set('--iti-hover-color', this.withAlpha(charter.principal, 0.08));
    set('--iti-icon-color', charter.principal);
    set('--iti-border-color', this.withAlpha(charter.primaryText, 0.1));
    set('--iti-country-selector-bg', charter.primaryCardBg);

    // Angular Material (prebuilt theme override)
    set('--mdc-theme-primary', charter.principal);
    set('--mdc-theme-on-primary', charter.lightText);
    set('--mat-toolbar-container-background-color', charter.principal);
    set('--mat-toolbar-container-text-color', charter.lightText);
  }

  getCurrent(): CharterColor {
    return this.current;
  }

  /** Palette décorative pour cartes (agences, actions rapides). */
  getAccentPalette(): string[] {
    const c = this.current;
    return [c.principal, c.secondary, c.info, c.success, c.warning, c.error];
  }

  private shade(hex: string, percent: number): string {
    const rgb = this.parseHex(hex);
    if (!rgb) {
      return hex;
    }
    const factor = 1 + percent / 100;
    const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v * factor)));
    return `#${[clamp(rgb.r), clamp(rgb.g), clamp(rgb.b)]
      .map((v) => v.toString(16).padStart(2, '0'))
      .join('')}`;
  }

  private withAlpha(hex: string, alpha: number): string {
    const rgb = this.parseHex(hex);
    if (!rgb) {
      return hex;
    }
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
  }

  private parseHex(hex: string): { r: number; g: number; b: number } | null {
    const normalized = hex.replace('#', '').trim();
    if (normalized.length !== 6) {
      return null;
    }
    return {
      r: parseInt(normalized.slice(0, 2), 16),
      g: parseInt(normalized.slice(2, 4), 16),
      b: parseInt(normalized.slice(4, 6), 16),
    };
  }
}
